/**
 * Settings → Data model → "Your data models" (docs/environments.md, Phase 2), laid out like the Data model tab beside it:
 * the models of your own (and, as a quiet group, the suggested ones) in a list on the left; the chosen one on the right
 * with its header, a few numbers and its field table — each field with the source fields of each system it is made of,
 * their type and whether this environment has seen them. "Show actual data" fills a column with what each system says
 * about one entity (the lens: the version deployed to the environment, else the working copy), entity by entity,
 * newest first, or the one looked up by id or value; values the systems disagree on are marked.
 * The Versions tab has the published versions and what each environment runs. Owners publish the working copy as the
 * next version and deploy (promote, roll back) versions to an environment after a preview with the diff and a drift
 * check; viewers read. Like the rest of Settings a publish or a deploy is a panel, never a dialog.
 * The view is in the URL (lib/modelsview.ts): choosing a model or a suggestion and switching its tab are history steps,
 * toggles replace the entry, so Back returns to the same view; a typed publish comment is kept in sessionStorage.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from '../lib/liveparams.ts';
import { useAuth } from '../lib/auth.tsx';
import { Api, ApiError, type ClassSnapshot, type DeployItem, type DeployPreview, type Environment, type Lens, type LensEntity, type ModelDiff, type ModelSuggestion, type ModelVersion, type VirtualModel } from '../lib/api.ts';
import { useEnv } from '../lib/envs.tsx';
import { useAsync } from '../lib/hooks.ts';
import { ago, count, dateTime, quote } from '../lib/format.ts';
import { openModel, openSuggestion, patchModels, readModels, readValues, sourceKey, splitSource, withValues, type ModelTab, type ModelsView } from '../lib/modelsview.ts';
import { useFormDraft, useSyncedText } from '../lib/formstate.ts';
import {
  actualValues, chipState, currentLabel, defaultTarget, deployKind, diffSummary, entitySearch, fieldSearch, fieldType, knownFields, latestItems, lensMode,
  modelState, modelSystems, seenSources, sourceCount, sourceSystem, splitPath, type KnownField,
} from '../lib/models.ts';
import { EDIT_KEY, FROM_KEY, NEW_MODEL, draftKey, emptyModel, fromSuggestion, readEdit, readFrom, suggestionDraftId, withEdit, withFrom } from '../lib/modeledit.ts';
import { hasForm } from '../lib/pagedraft.ts';
import { Confidence } from '../components/SuggestedModels.tsx';
import { DistinctValues } from '../components/DistinctValues.tsx';
import { useModalSteps, useUrlModal } from '../lib/urlmodal.ts';
import { ImportModels } from '../components/ImportModels.tsx';
import { IconButton, Kicker, Menu, Surface, SystemBadge, cx, noun } from '../design/primitives.tsx';
import { AlertIcon, ArrowIcon, BackIcon, ChevronIcon, DownloadIcon, FilterIcon, PencilIcon, PlusIcon, SchemaIcon, SearchIcon, TrashIcon } from '../components/icons.tsx';
import { ModelEditor } from '../components/ModelEditor.tsx';
import { EnvDot, EnvSelect } from '../components/EnvBits.tsx';
import { Button, Field, Spinner, inputClass, useToast } from '../components/ui.tsx';
import { Confirm, EXAMPLE_TINT, Marked, Panel, cap, td, th } from './Settings.tsx';

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
/** A change of the view: a history step (push) unless it is typing or tidying (replace). */
type Change = (patch: Partial<ModelsView>, how?: 'push' | 'replace') => void;

// ---------------------------------------------------------------- small pieces

/** What each environment runs of a model: a dot, the name and the version; muted when nothing is deployed, amber when behind. */
function EnvChips({ envs, deployed, latest }: { envs: Environment[]; deployed: Record<string, number> | undefined; latest: number | null | undefined }) {
  if (envs.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-1.5">
      {envs.map((e) => {
        const v = deployed?.[e.id];
        const state = chipState(v, latest);
        return (
          <span key={e.id} title={v === undefined ? `Not deployed to ${e.name}` : state === 'behind' ? `${e.name} runs v${v}; the latest is v${latest}` : `${e.name} runs v${v}`}
            className={cx('inline-flex items-center gap-1.5 h-6 px-2 rounded-full bg-n3 text-micro whitespace-nowrap', state === 'none' ? 'text-n7' : 'text-n9')}>
            <EnvDot color={e.color} className={cx(state === 'none' && 'opacity-40')} />
            {e.name}
            <span className={cx('font-mono', state === 'behind' && 'text-warn font-medium')}>{v === undefined ? '—' : `v${v}`}</span>
            {state === 'behind' && <span className="size-1.5 rounded-full bg-warn" aria-hidden="true" />}
          </span>
        );
      })}
    </span>
  );
}

function Tag({ tone, children }: { tone: 'warn' | 'plain'; children: React.ReactNode }) {
  return (
    <span className={cx('inline-flex items-center h-5 px-2 rounded-full text-micro font-medium whitespace-nowrap', tone === 'warn' ? 'bg-warn-soft text-warn' : 'bg-n3 text-n9 font-mono')}>
      {children}
    </span>
  );
}

const Sign = ({ c, children }: { c: '+' | '−'; children: React.ReactNode }) => (
  <div className="flex gap-2 items-baseline">
    <span className={cx('font-mono w-3 shrink-0 text-center', c === '+' ? 'text-ok' : 'text-fail')} aria-hidden="true">{c}</span>
    <span className="sr-only">{c === '+' ? 'added' : 'removed'}</span>
    <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
  </div>
);

const PathText = ({ k }: { k: string }) => {
  const { cls, path } = splitPath(k);
  return <span className="font-mono text-micro">{cls}<span className="text-n6">·</span>{path}</span>;
};

/** A diff in words: the rename, the fields that came and went, and the paths that changed within a field. */
function DiffView({ diff, empty = 'No changes.' }: { diff: ModelDiff; empty?: string }) {
  if (diff.same) return <p className="m-0 text-small text-n7">{empty}</p>;
  return (
    <div className="flex flex-col gap-1.5 text-small text-n9">
      {diff.initial && <p className="m-0 text-micro text-n7">Fields in this version</p>}
      {diff.renamed && <div>Renamed <span className="text-n8">{diff.renamed.from}</span> → <span className="font-medium">{diff.renamed.to}</span></div>}
      {diff.addedFields.map((f) => <Sign key={`+${f}`} c="+">{f}</Sign>)}
      {diff.removedFields.map((f) => <Sign key={`-${f}`} c="−">{f}</Sign>)}
      {diff.changedFields.map((c) => (
        <div key={c.label} className="flex flex-col gap-1">
          <span className="font-medium">{c.label} <span className="text-n7 font-normal">changed</span></span>
          <div className="pl-3 flex flex-col gap-1">
            {c.addedPaths.map((p) => <Sign key={`+${p}`} c="+"><PathText k={p} /></Sign>)}
            {c.removedPaths.map((p) => <Sign key={`-${p}`} c="−"><PathText k={p} /></Sign>)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- publish

function PublishPanel({ tid, model, onClose, onDone }: { tid: string; model: VirtualModel; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [{ comment }, setDraft, clearDraft] = useFormDraft(`publish.${tid}.${model.id}`, { comment: '' });
  const setComment = (c: string) => setDraft({ comment: c });
  const [busy, setBusy] = useState(false);
  const close = () => { clearDraft(); onClose(); };
  const done = () => { clearDraft(); onDone(); };
  const publish = async () => {
    setBusy(true);
    try {
      const v = await Api.publishModel(tid, model.id, comment.trim());
      toast(`${model.name} v${v.version} published`);
      done();
    } catch (e) {
      // Nothing changed since the last version: not an error, just nothing to do
      if (e instanceof ApiError && e.status === 409) { toast(e.message); done(); } else { toast(message(e), 'fail'); setBusy(false); }
    }
  };
  return (
    <Panel title={`Publish ${model.name}${model.version ? ` as v${model.version + 1}` : ' as v1'}`} onClose={close}>
      <p className="m-0 text-small text-n8 max-w-2xl">The working copy is frozen as the next version. A version never changes; environments run the version you deploy to them.</p>
      <form className="flex items-end gap-3 flex-wrap" onSubmit={(e) => { e.preventDefault(); void publish(); }}>
        <Field label="Comment (optional)" className="flex-1 min-w-[240px] max-w-xl">
          <input className={inputClass} autoFocus maxLength={500} placeholder="What changed and why" value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={busy}>Publish</Button>
        <Button variant="ghost" onClick={close}>Cancel</Button>
      </form>
    </Panel>
  );
}

// ---------------------------------------------------------------- deploy

function DriftList({ drift }: { drift: DeployPreview['items'][number]['drift'] }) {
  const notes = drift.filter((d) => d.kind === 'no_data');
  const warnings = drift.filter((d) => d.kind !== 'no_data');
  return (
    <>
      {warnings.length > 0 && (
        <div className="rounded-chip bg-warn-soft p-3 flex flex-col gap-1.5" role="group" aria-label="Warnings">
          <span className="flex items-center gap-2 text-small font-medium text-warn"><AlertIcon size={16} />{warnings.length === 1 ? '1 warning' : `${warnings.length} warnings`}<span className="font-normal text-n8">· they do not stop the deployment</span></span>
          <ul className="m-0 pl-6 text-small text-n9 flex flex-col gap-0.5">{warnings.map((w, i) => <li key={i}>{w.message}</li>)}</ul>
        </div>
      )}
      {notes.map((n, i) => <p key={i} className="m-0 text-small text-n8">{n.message}</p>)}
    </>
  );
}

/** Deploy (promote or roll back) the given versions to one environment: pick it, read the preview, confirm when protected. */
function DeployPanel({ tid, items, models, skipped, picked, onPick, onClose, onDone }: {
  tid: string; items: DeployItem[]; models: Map<string, VirtualModel>; skipped: string[]; picked: string | undefined; onPick: (envId: string) => void; onClose: () => void; onDone: () => void;
}) {
  const { envs } = useEnv();
  const toast = useToast();
  const sorted = useMemo(() => [...envs].sort((a, b) => a.position - b.position), [envs]);
  // The target is in the URL (`picked`); until it names one, the environment that suits the versions (worked out once the environments are known)
  const fallback = useRef<string | undefined>(undefined);
  if (fallback.current === undefined && sorted.length > 0) {
    const first = sorted.find((e) => items.some((i) => models.get(i.modelId)?.deployed?.[e.id] !== i.version));
    fallback.current = (items.length === 1 ? defaultTarget(sorted, models.get(items[0]!.modelId)?.deployed, items[0]!.version) : first ?? sorted[0])?.id ?? '';
  }
  const envId = (picked && sorted.some((e) => e.id === picked) ? picked : fallback.current) ?? '';
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const preview = useAsync(() => (envId ? Api.previewDeployment(tid, envId, items) : Promise.reject(new Error('This tenant has no environments'))), [tid, envId, JSON.stringify(items)]);
  const p = preview.data?.environment.id === envId ? preview.data : undefined;
  const kinds = p?.items.map((i) => deployKind(i.current, i.version)) ?? [];
  const changing = kinds.filter((k) => k !== 'same').length;
  const rollback = p?.items.length === 1 && kinds[0] === 'rollback';
  const name = p?.environment.name ?? sorted.find((e) => e.id === envId)?.name ?? '';

  const run = async () => {
    if (!p) return;
    setBusy(true);
    try {
      await Api.deploy(tid, envId, items, p.environment.protected ? true : undefined);
      toast(p.items.length === 1 ? `${p.items[0]!.name} v${p.items[0]!.version} ${rollback ? 'rolled back' : 'deployed'} to ${name}` : `${p.items.length} models deployed to ${name}`);
      onDone();
    } catch (e) {
      toast(message(e), 'fail');
      setBusy(false);
    }
  };

  const title = items.length === 1
    ? `${rollback ? 'Roll back' : 'Deploy'} ${models.get(items[0]!.modelId)?.name ?? ''} ${rollback ? 'to' : ''} v${items[0]!.version}`.replace('  ', ' ')
    : `Deploy ${items.length} models`;
  const button = !p ? 'Deploy' : rollback ? `Roll back to v${p.items[0]!.version}` : p.items.length === 1 ? `Deploy v${p.items[0]!.version} to ${name}` : `Deploy ${changing || p.items.length} models to ${name}`;

  return (
    <Panel title={title} onClose={onClose}>
      <div className="flex items-end gap-4 flex-wrap">
        <Field label="Target environment" className="w-64 max-w-full">
          <EnvSelect envs={sorted} value={envId} onChange={(id) => { onPick(id); setConfirmed(false); }} />
        </Field>
        {sorted.find((e) => e.id === envId) && <span className="pb-2 inline-flex items-center gap-2 text-small text-n8"><EnvDot color={sorted.find((e) => e.id === envId)?.color} />{name}{p?.environment.protected && ' · protected'}</span>}
      </div>
      {skipped.length > 0 && <p className="m-0 text-small text-n8">Not included, nothing published yet: {skipped.join(', ')}.</p>}
      {preview.error && !p && <p className="m-0 text-small text-fail">{preview.error}</p>}
      {!p && !preview.error && <div className="py-4 grid place-items-center"><Spinner /></div>}
      {p && (
        <div className={cx('flex flex-col gap-3', preview.loading && 'opacity-60')}>
          {p.items.map((it) => {
            const kind = deployKind(it.current, it.version);
            return (
              <div key={it.modelId} className="rounded-chip bg-n1/60 border border-n4 p-3 flex flex-col gap-2.5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-small font-medium">{it.name}</span>
                  <Tag tone="plain">v{it.version}</Tag>
                  <span className="text-small text-n8">{currentLabel(it.current)}{kind === 'update' || kind === 'new' ? ` → v${it.version}` : ''}</span>
                  {kind === 'rollback' && <Tag tone="warn">Roll back to v{it.version}</Tag>}
                  {kind === 'same' && <span className="text-small text-n7">· already runs this version, nothing changes</span>}
                </div>
                {kind !== 'same' && (
                  <div>
                    <p className="m-0 mb-1.5 text-micro uppercase tracking-[.08em] text-n7 font-medium">{it.current === null ? `New in ${p.environment.name}` : `Compared with v${it.current} in ${p.environment.name}`}</p>
                    <DiffView diff={it.diff} />
                  </div>
                )}
                <DriftList drift={it.drift} />
              </div>
            );
          })}
          {p.environment.protected && (
            <label className="flex items-center gap-2.5 text-small cursor-pointer select-none rounded-chip bg-warn-soft px-3 py-2.5">
              <input type="checkbox" className="size-4 accent-[var(--warn)]" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              <span><span className="font-medium">Deploy to {p.environment.name}.</span> <span className="text-n8">{p.environment.name} is protected; I confirm this change.</span></span>
            </label>
          )}
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <Button variant="primary" disabled={!p || busy || changing === 0 || (p.environment.protected && !confirmed)} onClick={() => void run()}>
          {busy ? 'Deploying…' : changing === 0 && p ? 'Nothing to deploy' : button}
        </Button>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </Panel>
  );
}


function VersionRow({ v, envs, owner, open, onToggle, onDeploy }: { v: ModelVersion; envs: Environment[]; owner: boolean; open: boolean; onToggle: () => void; onDeploy: () => void }) {
  const runs = envs.filter((e) => v.deployedTo.includes(e.id));
  return (
    <li className="py-3 flex flex-col gap-2 border-t border-n4 first:border-t-0">
      <div className="flex items-start gap-3 flex-wrap">
        <Tag tone="plain">v{v.version}</Tag>
        <div className="flex-1 min-w-[200px] flex flex-col gap-1">
          <span className={cx('text-small [overflow-wrap:anywhere]', v.comment ? 'text-n10' : 'text-n7')}>{v.comment ?? 'No comment'}</span>
          <span className="text-micro text-n7">{v.createdBy ? `${v.createdBy} · ` : ''}{ago(v.createdAt)}</span>
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-micro text-n8">
            {runs.length > 0 ? <>Runs in {runs.map((e) => <span key={e.id} className="inline-flex items-center gap-1.5"><EnvDot color={e.color} />{e.name}</span>)}</> : <span className="text-n7">Not deployed</span>}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" aria-expanded={open} onClick={onToggle}>
            {diffSummary(v.diff)}<ChevronIcon size={14} className={cx('transition-transform duration-[var(--t-std)] ease-mv', !open && '-rotate-90')} />
          </Button>
          {owner && <Button onClick={onDeploy}>Deploy…</Button>}
        </div>
      </div>
      {open && <div className="pl-1 pb-1 mv-rise"><DiffView diff={v.diff} /></div>}
    </li>
  );
}

// ---------------------------------------------------------------- the field table

/** A row of the field table: a field of a model (or of a suggestion) and its source fields. */
interface Row { label: string; paths: { cls: string; path: string; system?: string | null }[]; note?: string }

const menuTrigger = 'size-8 rounded-full grid place-items-center bg-transparent text-n8 border-0 cursor-pointer text-body leading-none hover:bg-n3 focus-visible:ring-2 focus-visible:ring-accent';

/** A path that wraps after its slashes rather than inside a name. */
const PathBreaks = ({ path }: { path: string }) => <>{path.split('/').map((part, i) => <Fragment key={i}>{i > 0 && <>/<wbr /></>}{part}</Fragment>)}</>;

/** A source field: the system, the data model's name and the path; a dot when this environment has not seen it. */
function SourceLine({ p, seen, envName }: { p: Row['paths'][number]; seen: boolean; envName: string }) {
  return (
    <span className="flex items-start gap-1.5 min-w-0" title={`${p.cls} · ${p.path}${seen ? '' : ` — not seen in ${envName}`}`}>
      <SystemBadge code={sourceSystem({ cls: p.cls, system: p.system ?? undefined })} size={20} className="shrink-0" />
      <span className="min-w-0 leading-5 [overflow-wrap:break-word]">
        {!seen && <span className="inline-block mr-1.5 size-1.5 rounded-full bg-warn align-middle" role="img" aria-label={`not seen in ${envName}`} />}
        <span className={seen ? 'text-n8' : 'text-n7'}>{cap(noun(p.cls))}</span>
        <span className="text-n6" aria-hidden="true"> · </span>
        <span className={cx('font-mono text-micro', seen ? 'text-n10' : 'text-n8')}><PathBreaks path={p.path} /></span>
      </span>
    </span>
  );
}

/** The actual values of a field for one entity, one line per system; values the systems disagree on are marked. */
function ActualCell({ row, entity, lens, term, version }: { row: Row; entity: LensEntity; lens: Lens; term: string | undefined; version: string }) {
  if (!lens.fields.some((f) => f.label === row.label)) return <span className="text-micro text-n6" title={`${row.label} is not in ${version}`}>Not in {version}</span>;
  const values = actualValues(row.paths.map((p) => ({ cls: p.cls, system: p.system ?? undefined })), entity.cells[row.label], entity.systems);
  if (values.length === 0) return <span className="text-n6">—</span>;
  return (
    <span className="flex flex-col gap-1">
      {values.map((v) => (
        <span key={v.system} className="flex items-start gap-1.5 min-w-0">
          <SystemBadge code={v.system} size={20} className="shrink-0" />
          {v.value === null
            ? <span className="leading-5 text-n6" title={`No value in ${v.system}`}>—</span>
            : (
              <span data-differs={v.differs ? '1' : undefined} title={v.differs ? 'Differs from what the other systems say' : undefined}
                className={cx('leading-5 min-w-0 [overflow-wrap:anywhere]', v.differs && 'rounded px-1 -mx-1 bg-warn-soft text-n10 underline decoration-warn decoration-dotted underline-offset-4')}>
                <Marked text={v.value} term={term} />
              </span>
            )}
        </span>
      ))}
    </span>
  );
}

function FieldTable({ rows, known, envName, actual, keyLabel, menu, empty }: {
  rows: Row[]; known: ReadonlyMap<string, KnownField>; envName: string;
  /** The actual data column: the entity shown, or a reason it is empty. */
  actual?: { lens?: Lens; entity?: LensEntity; term?: string; version: string; placeholder?: ReactNode };
  keyLabel?: string;
  menu?: (row: Row) => ReactNode;
  empty?: ReactNode;
}) {
  return (
    <Surface className="overflow-x-auto" data-hscroll>
      <table className={cx('w-full border-collapse', actual ? 'min-w-[820px]' : 'min-w-[640px]')} aria-label="Fields">
        <thead>
          <tr>
            <th className={cx(th, 'min-w-36')}>Field</th>
            {actual && <th className={cx(th, EXAMPLE_TINT, 'min-w-60')}>Actual data</th>}
            <th className={cx(th, 'min-w-56')}>Sources</th>
            <th className={th}>Type</th>
            <th className={th}>In {envName}</th>
            {menu && <th className={th}><span className="sr-only">Actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const type = fieldType(r.paths, known);
            const seen = seenSources(r.paths, known);
            return (
              <tr key={r.label} className="hover:bg-n3/60" data-field={r.label}>
                <td className={cx(td, 'text-n10 font-medium [overflow-wrap:anywhere]')}>
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    {r.label}
                    {keyLabel === r.label && <span className="h-5 px-1.5 rounded-full bg-n3 text-micro text-n8 font-normal inline-flex items-center" title="The entities are matched by this field">key</span>}
                  </span>
                  {r.note && <span className="block mt-0.5 text-micro text-n7 font-normal">{r.note}</span>}
                </td>
                {actual && (
                  <td className={cx(td, EXAMPLE_TINT, 'max-w-80 text-small text-n10')}>
                    {actual.lens && actual.entity ? <ActualCell row={r} entity={actual.entity} lens={actual.lens} term={actual.term} version={actual.version} /> : actual.placeholder ?? <span className="text-n6">—</span>}
                  </td>
                )}
                <td className={td}>
                  <span className="flex flex-col gap-1.5">
                    {r.paths.map((p) => <SourceLine key={`${p.cls}|${p.path}`} p={p} seen={known.has(`${p.cls}|${p.path}`)} envName={envName} />)}
                  </span>
                </td>
                <td className={cx(td, 'whitespace-nowrap')}>
                  {type.label
                    ? <span className={cx('font-mono text-micro uppercase', type.mixed ? 'text-warn' : 'text-n8')} title={type.mixed ? `The sources have different types: ${type.types.join(', ').toLowerCase()}` : undefined}>{type.label}</span>
                    : <span className="text-micro text-n7">{seen.seen ? 'not enough values yet' : '—'}</span>}
                </td>
                <td className={cx(td, 'whitespace-nowrap')}>
                  <span className={cx('inline-flex items-center gap-1.5 text-small tabular-nums', seen.missing.length ? 'text-n9' : 'text-n8')}
                    title={seen.missing.length ? `Not seen in ${envName}: ${seen.missing.map((p) => `${p.cls} · ${p.path}`).join(', ')}` : `${envName} has all its sources`}>
                    {seen.missing.length > 0 && <span className="size-1.5 rounded-full bg-warn" aria-hidden="true" />}
                    {seen.seen} of {seen.total}
                  </span>
                </td>
                {menu && <td className={cx(td, 'text-right py-1')}>{menu(r)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && empty}
    </Surface>
  );
}

// ---------------------------------------------------------------- actual data (the lens)

/** The entities a model finds in this environment, newest first, or those an id or value finds. */
function useLens(tid: string, m: VirtualModel, envId: string | undefined, view: ModelsView) {
  const mode = lensMode(m.deployed, envId, view.ldraft);
  // (once the environment is known: which version it runs decides what is asked)
  const enabled = view.actual && view.tab === 'fields' && !!envId;
  const lens = useAsync(() => {
    if (!enabled) return Promise.resolve(undefined);
    const p = new URLSearchParams({ sort: 'newest' });
    if (mode.draft) p.set('draft', '1');
    if (view.find) p.set('q', view.find);
    return Api.lens(tid, m.id, p);
  }, [tid, m.id, envId, enabled, mode.draft, mode.version, view.find, m.updatedAt]);
  // A lens of the model looked at before stays out while this one loads
  const data = lens.data?.model.id === m.id ? lens.data : undefined;
  const list = data?.entities ?? [];
  const entity = list.length ? list[Math.min(view.entity, list.length - 1)] : undefined;
  const index = entity ? list.indexOf(entity) : 0;
  return { enabled, mode, lens, data, list, entity, index };
}
type LensState = ReturnType<typeof useLens>;

/** The bar above the field table: switch the actual data on, step through the entities, look one up by id or value. */
function ActualBar({ m, envName, lens, view, change }: { m: VirtualModel; envName: string; lens: LensState; view: ModelsView; change: Change }) {
  const navigate = useNavigate();
  const { link } = useEnv();
  const [text, setText] = useState(view.find ?? '');
  useEffect(() => setText(view.find ?? ''), [view.find]);
  const on = view.actual;
  const { data, list, entity, index, mode } = lens;
  const find = (q: string) => change({ entity: 0, find: q.trim() || undefined });
  const draftNote = mode.reason === 'not-deployed'
    ? <>Not deployed to {envName} — showing the draft.</>
    : mode.reason === 'asked'
      ? <>Showing the draft · {envName} runs v{mode.version}. <button type="button" className="p-0 border-0 bg-transparent text-accent cursor-pointer hover:underline" onClick={() => change({ ldraft: false, entity: 0 })}>Show v{mode.version}</button></>
      : m.dirty
        ? <>{envName} runs v{mode.version}; the draft has changes. <button type="button" className="p-0 border-0 bg-transparent text-accent cursor-pointer hover:underline" onClick={() => change({ ldraft: true, entity: 0 })}>Preview the draft</button></>
        : null;
  const facts: [string, ReactNode, boolean, string?][] = entity && data ? [
    ...(entity.title !== entity.key ? [['Entity', <Marked key="t" text={entity.title} term={view.find} />, false, entity.title] as [string, ReactNode, boolean, string?]] : []),
    [data.key, <Marked key="k" text={entity.key} term={view.find} />, true, entity.key],
    ['Found in', <span key="s" className="inline-flex items-center gap-1">{entity.systems.map((s) => <SystemBadge key={s} code={s} size={20} />)}</span>, false],
    ['Last seen', dateTime(entity.last), false],
    ['Records', count(entity.records), false],
    ['Systems disagree', entity.conflicts ? <span key="c" className="inline-flex items-center gap-1.5 text-warn"><span className="size-1.5 rounded-full bg-warn" aria-hidden="true" />{entity.conflicts === 1 ? '1 field' : `${count(entity.conflicts)} fields`}</span> : entity.systems.length > 1 ? 'on nothing' : 'only one system', false],
  ] : [];
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3 flex-wrap">
        <Button aria-pressed={on} onClick={() => { setText(''); change({ actual: !on, entity: 0, find: undefined, ldraft: false }); }}>{on ? 'Hide actual data' : 'Show actual data'}</Button>
        {on && (
          <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); find(text); }}>
            <input className={cx(inputClass, 'w-64')} placeholder="Find an entity by id or value" aria-label="Find an entity by id or value" value={text} onChange={(e) => setText(e.target.value)} />
            <Button type="submit" disabled={!text.trim()}>Find</Button>
            {view.find && <Button variant="ghost" onClick={() => { setText(''); find(''); }}>Show newest</Button>}
          </form>
        )}
      </div>
      {on && (
        <div className={cx('rounded-card px-4 py-3 flex flex-col gap-3', EXAMPLE_TINT)} role="region" aria-label="Actual data">
          {draftNote && <p className="m-0 text-small text-n8 flex items-center gap-2" data-lens-mode={mode.draft ? 'draft' : 'deployed'}><span className="size-1.5 rounded-full bg-warn shrink-0" aria-hidden="true" /><span>{draftNote}</span></p>}
          {lens.lens.error && !data && <p className="m-0 text-small text-fail">{lens.lens.error}</p>}
          {!data && !lens.lens.error && <Spinner />}
          {data && data.vaultErrors.length > 0 && <p className="m-0 text-small text-warn">Not all Vaults answered: {data.vaultErrors.map((e) => e.vaultName).join(', ')}.</p>}
          {data && list.length === 0 && (
            <p className="m-0 text-small text-n8">{view.find ? `No entity of ${m.name} matches “${view.find}”.` : `${m.name} finds no entities in ${envName} yet: its key field has no values here.`}</p>
          )}
          {data && entity && (
            <>
              <div className={cx('flex items-center gap-2', lens.lens.loading && 'opacity-60')}>
                <IconButton label="Previous entity" disabled={index <= 0} onClick={() => change({ entity: index - 1 })}><BackIcon size={16} /></IconButton>
                <span className="text-small tabular-nums text-n10 font-medium" data-entity-pos={index}>
                  {view.find ? `Match ${index + 1} of ${count(data.total)}` : `Entity ${index + 1} of ${count(list.length)}`}
                </span>
                <IconButton label="Next entity" disabled={index >= list.length - 1} onClick={() => change({ entity: index + 1 })}><ArrowIcon size={16} /></IconButton>
                <span className="text-micro text-n7">{!view.find && data.total > list.length ? `the newest of ${count(data.total)} entities` : !view.find ? 'newest first' : ''}</span>
                <span className="flex-1" />
                <Menu.Root>
                  <Menu.Trigger aria-label="Entity actions" className={menuTrigger}><span aria-hidden="true">⋯</span></Menu.Trigger>
                  <Menu.Content align="end" className="min-w-72">
                    <Menu.Label>Leaves Settings</Menu.Label>
                    <Menu.Item icon={<SearchIcon size={16} />} onSelect={() => void navigate(link(`/s?${new URLSearchParams({ q: entitySearch(entity.key, entity.cells[data.key]), range: 'all' })}`))}>Open in the data search</Menu.Item>
                  </Menu.Content>
                </Menu.Root>
              </div>
              <dl className="m-0 grid gap-x-6 gap-y-2.5 grid-cols-[repeat(auto-fill,minmax(160px,1fr))]">
                {facts.map(([label, value, mono, title]) => (
                  <div key={label} className="flex flex-col gap-0.5 min-w-0">
                    <dt className="text-micro uppercase tracking-[.08em] text-n7 truncate">{label}</dt>
                    <dd className={cx('m-0 text-small text-n10 truncate', mono && 'font-mono text-micro leading-5')} title={title}>{value}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- one model

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <Kicker>{label}</Kicker>
      <span className="text-title font-semibold tabular-nums">{children}</span>
    </div>
  );
}

function StateChip({ m }: { m: Pick<VirtualModel, 'version' | 'dirty'> }) {
  const s = modelState(m);
  return <span title={s.title}><Tag tone={s.tone}>{s.text}</Tag></span>;
}

function SubTabs({ name, tab, versions, onTab }: { name: string; tab: ModelTab; versions: number | undefined; onTab: (t: ModelTab) => void }) {
  const tabs: [ModelTab, string][] = [['fields', 'Fields'], ['versions', `Versions${versions !== undefined ? ` (${versions})` : ''}`]];
  return (
    <div role="tablist" aria-label={`${name} sections`} className="flex gap-1">
      {tabs.map(([id, label]) => (
        <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => onTab(id)}
          className={cx('h-8 px-3 rounded-chip border-0 text-small cursor-pointer transition-colors duration-[var(--t-micro)] ease-mv',
            tab === id ? 'bg-n3 text-n10 font-medium' : 'bg-transparent text-n8 hover:text-n10 hover:bg-n3/60')}>{label}</button>
      ))}
    </div>
  );
}

function ModelView({ tid, m, envs, known, owner, view, change, onTab, onChanged, onEdit, onDelete, onExport }: {
  tid: string; m: VirtualModel; envs: Environment[]; known: ReadonlyMap<string, KnownField>; owner: boolean; view: ModelsView;
  change: Change; onTab: (t: ModelTab) => void; onChanged: () => void; onEdit: () => void; onDelete: () => void; onExport: () => void;
}) {
  const navigate = useNavigate();
  const { current, link, visible } = useEnv();
  const [, setParams] = useSearchParams();
  const envName = current?.name ?? 'this environment';
  const panel = owner && view.panel === 'publish' ? { kind: 'publish' as const } : owner && view.panel === 'deploy' && view.dv ? { kind: 'deploy' as const, version: view.dv } : undefined;
  // Publish and deploy panels are steps in history: Back closes them, closing goes back
  const panelWin = useModalSteps('panelOpened');
  const openPanel = (patch: Partial<ModelsView>) => panelWin.open((p) => patchModels(p, patch));
  const closePanel = () => panelWin.close((p) => patchModels(p, { panel: undefined, dv: undefined, denv: undefined }));
  const versions = useAsync(() => Api.modelVersions(tid, m.id), [tid, m.id, m.updatedAt, m.version, m.dirty, JSON.stringify(m.deployed)]);
  const models = useMemo(() => new Map([[m.id, m]]), [m]);
  const draft = versions.data?.draft;
  const lens = useLens(tid, m, current?.id, view);
  const [filter, setFilter] = useSyncedText(view.filter, (t) => change({ filter: t }, 'replace'));
  const q = filter.trim().toLowerCase();
  const rows: Row[] = m.fields.filter((f) => !q || f.label.toLowerCase().includes(q) || f.paths.some((p) => `${p.cls} ${p.path}`.toLowerCase().includes(q)));
  const systems = modelSystems(m);
  const sources = sourceCount(m);
  // The distinct values of a source field: a step in history (Back closes it)
  const values = useUrlModal('mvf', readValues, withValues);
  const [valueQuery, setValueQuery] = useSyncedText(view.mvq, (t) => change({ mvq: t, mvn: 50 }, 'replace'));
  const open = values.value ? splitSource(values.value) : undefined;
  const openField = open ? known.get(sourceKey(open.cls, open.path)) : undefined;
  const version = lens.mode.draft ? 'the draft' : `v${lens.mode.version}`;
  const search = (q: string) => void navigate(link(`/s?${new URLSearchParams({ q, range: 'all' })}`));
  const deployed = sortedEnvs(envs);

  return (
    <div className="flex flex-col gap-4 min-w-0" data-model={m.id}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="m-0 text-title font-semibold tracking-tight [overflow-wrap:anywhere]">{m.name}</h2>
            <StateChip m={m} />
            {systems.length > 0 && <span className="flex gap-1">{systems.map((s) => <SystemBadge key={s} code={s} size={20} />)}</span>}
          </div>
          <span className="text-micro text-n7">Your data model · updated {ago(m.updatedAt)}{lens.data ? <> · matched by {lens.data.key}</> : null}</span>
        </div>
        {owner && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={onEdit} aria-label={`Edit ${m.name}`}><PencilIcon size={14} />Edit…</Button>
            {(m.dirty || m.version == null) && <Button onClick={() => openPanel({ panel: 'publish', dv: undefined, denv: undefined })}>Publish version…</Button>}
            {m.version != null && <Button onClick={() => openPanel({ panel: 'deploy', dv: m.version!, denv: undefined })} title={`Deploy v${m.version} (or roll back from the Versions tab)`}>Deploy…</Button>}
            <Menu.Root>
              <Menu.Trigger aria-label={`More for ${m.name}`}
                className="size-9 rounded-chip grid place-items-center bg-n3 text-n9 border-0 cursor-pointer text-body leading-none hover:bg-n4 focus-visible:ring-2 focus-visible:ring-accent">
                <span aria-hidden="true">⋯</span>
              </Menu.Trigger>
              <Menu.Content align="end" className="min-w-60">
                <Menu.Item icon={<DownloadIcon size={16} />} onSelect={onExport}>Export as JSON</Menu.Item>
                <Menu.Separator />
                <Menu.Item icon={<TrashIcon size={16} />} onSelect={onDelete}>Delete…</Menu.Item>
              </Menu.Content>
            </Menu.Root>
          </div>
        )}
      </div>
      {panel?.kind === 'publish' && <PublishPanel tid={tid} model={m} onClose={closePanel} onDone={() => { closePanel(); onChanged(); }} />}
      {panel?.kind === 'deploy' && <DeployPanel tid={tid} items={[{ modelId: m.id, version: panel.version }]} models={models} skipped={[]} picked={view.denv} onPick={(id) => change({ denv: id }, 'replace')} onClose={closePanel} onDone={() => { closePanel(); onChanged(); }} />}
      <div className="flex gap-8 flex-wrap items-start">
        <Stat label="Fields">{count(m.fields.length)}</Stat>
        <Stat label="Sources">{count(sources)}</Stat>
        <Stat label="Systems">{count(systems.length)}</Stat>
        {lens.data && !view.find && <Stat label="Entities">{count(lens.data.total)}</Stat>}
        {deployed.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <Kicker>Deployed</Kicker>
            <EnvChips envs={deployed} deployed={m.deployed} latest={m.version} />
          </div>
        )}
      </div>
      <SubTabs name={m.name} tab={view.tab} versions={versions.data?.versions.length} onTab={onTab} />
      {view.tab === 'fields' && (
        <>
          <ActualBar m={m} envName={envName} lens={lens} view={view} change={change} />
          {m.fields.length > 8 && (
            <label className="flex items-center gap-2 h-9 px-3 rounded-chip bg-n3 text-small text-n8 w-full max-w-xs">
              <FilterIcon size={14} /><span className="sr-only">Filter fields</span>
              <input className="flex-1 min-w-0 bg-transparent border-0 outline-none text-n10 placeholder:text-n7" placeholder="Filter fields" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </label>
          )}
          <FieldTable rows={rows} known={known} envName={visible ? envName : 'this environment'} keyLabel={lens.data?.key}
            actual={view.actual ? { lens: lens.data, entity: lens.entity, term: view.find, version, placeholder: lens.data ? <span className="text-n6">—</span> : <span className="text-micro text-n7">…</span> } : undefined}
            empty={(
              <div className="p-6 flex flex-col items-center gap-3 text-small text-n7 text-center">
                {m.fields.length === 0 ? <span>No fields yet.{owner ? ' Edit the model to add them.' : ''}</span> : (
                  <>
                    <span>No field matches “{filter}”.</span>
                    <Button onClick={() => setFilter('')}>Show all {count(m.fields.length)} fields</Button>
                  </>
                )}
              </div>
            )}
            menu={(r) => (
              <Menu.Root>
                <Menu.Trigger aria-label={`Actions for ${r.label}`} className={menuTrigger}><span aria-hidden="true">⋯</span></Menu.Trigger>
                <Menu.Content align="end" className="min-w-72">
                  <Menu.Label>Show distinct values</Menu.Label>
                  {r.paths.map((p) => (
                    <Menu.Item key={`${p.cls}|${p.path}`} icon={<FilterIcon size={16} />} disabled={!known.has(sourceKey(p.cls, p.path))}
                      onSelect={() => values.open(sourceKey(p.cls, p.path))}>
                      <span className="inline-flex items-center gap-1.5 min-w-0">
                        <SystemBadge code={sourceSystem({ cls: p.cls, system: p.system ?? undefined })} size={20} />
                        <span className="truncate">{cap(noun(p.cls))} · <span className="font-mono text-micro">{p.path}</span></span>
                      </span>
                    </Menu.Item>
                  ))}
                  <Menu.Separator />
                  <Menu.Label>Opens the data search, leaving Settings</Menu.Label>
                  <Menu.Item icon={<SearchIcon size={16} />} onSelect={() => search(fieldSearch(r.paths))}>Search data with this field</Menu.Item>
                </Menu.Content>
              </Menu.Root>
            )} />
        </>
      )}
      {view.tab === 'versions' && (
        <div className="flex flex-col gap-4">
          {(m.dirty ?? false) && (
            <div className="rounded-chip bg-warn-soft p-3 flex flex-col gap-2" role="group" aria-label="Draft changes">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="flex-1 min-w-[200px] text-small"><span className="font-medium text-warn">Draft changes</span> <span className="text-n8">· {m.version ? `the working copy differs from v${m.version}` : 'nothing is published yet'}</span></span>
                {owner && <Button variant="primary" onClick={() => openPanel({ panel: 'publish', dv: undefined, denv: undefined })}>Publish version…</Button>}
              </div>
              {draft && <DiffView diff={draft.diff} />}
              {versions.loading && !draft && <Spinner />}
            </div>
          )}
          <Surface className="px-4 py-1">
            {versions.error && <p className="my-3 text-small text-fail">{versions.error}</p>}
            {versions.loading && !versions.data && <div className="py-4 grid place-items-center"><Spinner /></div>}
            {versions.data && versions.data.versions.length === 0 && <p className="my-3 text-small text-n7">No version published yet. {owner ? 'Publish the working copy to be able to deploy it.' : 'An owner publishes versions.'}</p>}
            {versions.data && versions.data.versions.length > 0 && (
              <ul className="m-0 p-0 list-none">
                {versions.data.versions.map((v) => <VersionRow key={v.id} v={v} envs={deployed} owner={owner} open={view.vx.includes(v.version)}
                  onToggle={() => change({ vx: view.vx.includes(v.version) ? view.vx.filter((x) => x !== v.version) : [...view.vx, v.version] })}
                  onDeploy={() => openPanel({ panel: 'deploy', dv: v.version, denv: undefined })} />)}
              </ul>
            )}
          </Surface>
        </div>
      )}
      {open && openField && (
        <DistinctValues tid={tid} cls={open.cls} path={open.path} fieldCount={openField.count}
          view={{ vs: view.mvs, vq: valueQuery, vn: view.mvn }}
          onView={(patch) => {
            const next = { ...(patch.vs ? { mvs: patch.vs } : {}), ...(patch.vq !== undefined ? { mvq: patch.vq } : {}), ...(patch.vn ? { mvn: patch.vn } : {}) };
            // A sort is a step of the values window; showing more of them replaces
            if (patch.vs) values.step((p) => patchModels(p, next));
            else change(next, 'replace');
          }}
          onQuery={setValueQuery} onClose={values.close}
          onActual={(value) => setParams((p) => patchModels(p, { mvf: undefined, mvq: '', mvn: 50, mvs: 'common', tab: 'fields', actual: true, entity: 0, find: value }), { state: {} })}
          onSearch={(value) => search(`@class=${quote(open.cls)} ${open.path}="${value.replace(/"/g, '')}"`)} />
      )}
    </div>
  );
}

const sortedEnvs = (envs: Environment[]) => [...envs].sort((a, b) => a.position - b.position);

/** A suggested data model, shown like a model: what it is made of, with what the suggestion is based on. */
function SuggestionView({ s, known, owner, envName, onCreate, onDismiss }: {
  s: ModelSuggestion; known: ReadonlyMap<string, KnownField>; owner: boolean; envName: string; onCreate: () => void; onDismiss: () => void;
}) {
  const systems = [...new Set(s.classes.map((c) => c.system).filter((x): x is string => !!x))];
  const rows: Row[] = s.fields.map((f) => ({ label: f.label, paths: f.sources, note: f.evidence }));
  const sources = rows.reduce((n, r) => n + r.paths.length, 0);
  return (
    <div className="flex flex-col gap-4 min-w-0" data-suggestion-view={s.id}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="m-0 text-title font-semibold tracking-tight [overflow-wrap:anywhere]">{s.name}</h2>
            <span data-suggested="1" title="Found in your data; not a data model of yours until you create a draft from it"
              className="inline-flex items-center h-5 px-2 rounded-full border border-dashed border-n6 text-micro text-n8 whitespace-nowrap cursor-help">Suggested</span>
            {s.isNew && <span className="inline-flex items-center h-5 px-2 rounded-full bg-accent-soft text-accent text-micro font-semibold">New</span>}
            {systems.length > 0 && <span className="flex gap-1">{systems.map((x) => <SystemBadge key={x} code={x} size={20} />)}</span>}
          </div>
          <span className="text-micro text-n7 flex items-center gap-2 flex-wrap"><Confidence value={s.confidence} />{s.evidence.length > 0 && <span>· {s.evidence.join(' · ')}</span>}</span>
        </div>
        {owner && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={onCreate} aria-label={`Create a draft from ${s.name}`}><PlusIcon size={14} />Create draft…</Button>
            <Button variant="ghost" onClick={onDismiss} aria-label={`Dismiss ${s.name}`}>Dismiss</Button>
          </div>
        )}
      </div>
      <p className="m-0 text-small text-n8 max-w-3xl">
        Found in your data: fields of several systems that hold the same values. {owner ? 'Create a draft to check and edit it before it is saved; nothing changes until you publish and deploy it.' : 'An owner of this tenant can turn it into a data model.'}
      </p>
      <div className="flex gap-8 flex-wrap">
        <Stat label="Fields">{count(rows.length)}</Stat>
        <Stat label="Sources">{count(sources)}</Stat>
        <Stat label="Systems">{count(systems.length)}</Stat>
      </div>
      <FieldTable rows={rows} known={known} envName={envName} />
    </div>
  );
}

// ---------------------------------------------------------------- the list

const groupHead = 'w-full flex items-center gap-1.5 px-2 pt-2.5 pb-1 border-0 bg-transparent text-left text-micro font-semibold uppercase tracking-wide text-n7';

function ModelItem({ m, chosen, owner, ticked, envs, onChoose, onTick }: {
  m: VirtualModel; chosen: boolean; owner: boolean; ticked: boolean; envs: Environment[]; onChoose: () => void; onTick: () => void;
}) {
  const systems = modelSystems(m);
  return (
    <div id={`vm-${m.id}`} className={cx('flex items-start rounded-chip scroll-mt-4 transition-colors duration-[var(--t-micro)]', chosen ? 'bg-accent-soft' : 'hover:bg-n3')}>
      {owner && (
        <label className="pl-2.5 pt-2 h-8 grid place-items-start shrink-0 cursor-pointer">
          <input type="checkbox" className="size-4 accent-[var(--accent)] cursor-pointer" aria-label={`Select ${m.name}`} checked={ticked} onChange={onTick} />
        </label>
      )}
      <button type="button" onClick={onChoose} aria-pressed={chosen} data-model-item={m.id}
        className={cx('flex-1 min-w-0 text-left py-2 pr-3 rounded-chip cursor-pointer border-0 bg-transparent flex flex-col gap-0.5', owner ? 'pl-2' : 'pl-3')}>
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="block text-small font-medium text-n10 truncate">{m.name}</span>
          <StateChip m={m} />
        </span>
        <span className="block text-micro text-n7">{m.fields.length === 1 ? '1 field' : `${count(m.fields.length)} fields`} · {count(sourceCount(m))} sources</span>
        {(systems.length > 0 || envs.length > 0) && (
          <span className="flex items-center gap-2 mt-1 min-w-0 flex-wrap">
            {systems.length > 0 && <span className="flex gap-1">{systems.map((s) => <SystemBadge key={s} code={s} size={20} />)}</span>}
            {envs.length > 1 && (
              <span className="flex items-center gap-1" aria-label="Deployed to">
                {envs.map((e) => {
                  const v = m.deployed?.[e.id];
                  const state = chipState(v, m.version);
                  return (
                    <span key={e.id} title={v === undefined ? `Not deployed to ${e.name}` : state === 'behind' ? `${e.name} runs v${v}; the latest is v${m.version}` : `${e.name} runs v${v}`}
                      className={cx('inline-flex rounded-full', state === 'behind' && 'ring-2 ring-warn/60')}>
                      <EnvDot color={e.color} className={cx(state === 'none' && 'opacity-25')} />
                    </span>
                  );
                })}
              </span>
            )}
          </span>
        )}
      </button>
    </div>
  );
}

function SuggestionItem({ s, chosen, onChoose }: { s: ModelSuggestion; chosen: boolean; onChoose: () => void }) {
  const systems = [...new Set(s.classes.map((c) => c.system).filter((x): x is string => !!x))];
  return (
    <button type="button" onClick={onChoose} aria-pressed={chosen} data-suggestion={s.id}
      className={cx('w-full text-left px-3 py-2 rounded-chip cursor-pointer border-0 flex flex-col gap-0.5 transition-colors duration-[var(--t-micro)]', chosen ? 'bg-accent-soft' : 'bg-transparent hover:bg-n3')}>
      <span className="flex items-center gap-1.5 min-w-0">
        <span className="block text-small text-n9 truncate">{s.name}</span>
        {s.isNew && <span className="inline-flex items-center h-5 px-1.5 rounded-full bg-accent-soft text-accent text-micro font-semibold shrink-0">New</span>}
      </span>
      <span className="flex items-center gap-2 min-w-0 flex-wrap">
        {systems.length > 0 && <span className="flex gap-1">{systems.map((x) => <SystemBadge key={x} code={x} size={20} />)}</span>}
        <span className="text-micro text-n7">{s.fields.length === 1 ? '1 field' : `${s.fields.length} fields`}</span>
        <Confidence value={s.confidence} short />
      </span>
    </button>
  );
}

/** The data models of your own: a name and, per field, the fields of each system it is made of; versions and what each environment runs. */
export function VirtualModels({ tid, reloadKey = 0, embedded = false, onCount, classes }: {
  tid: string; /** Changes when something outside saved a model (the list is read again). */ reloadKey?: number;
  /** Shown as a tab of its own (no space above). */ embedded?: boolean;
  /** Told how many models there are whenever the list is read. */ onCount?: (n: number) => void;
  /** The discovered model of the environment (types of the source fields, which ones it has seen). */ classes?: ClassSnapshot[];
}) {
  const toast = useToast();
  const { canManage } = useAuth();
  const { envs, current, visible } = useEnv();
  const sorted = useMemo(() => sortedEnvs(envs), [envs]);
  const models = useAsync(() => Api.virtualModels(tid), [tid, reloadKey]);
  // The discovered model, when the caller does not give it
  const own = useAsync(() => (classes ? Promise.resolve(undefined) : Api.model(tid)), [tid, current?.id, !!classes]);
  const known = useMemo(() => knownFields(classes ?? own.data?.classes), [classes, own.data]);
  // Suggested data models: asked again every few seconds while the Cloud is still checking candidates by their values
  const [suggestTick, setSuggestTick] = useState(0);
  const suggestions = useAsync(() => Api.modelSuggestions(tid), [tid, current?.id, reloadKey, suggestTick]);
  const partial = !!suggestions.data?.partial;
  const polls = useRef(0);
  useEffect(() => {
    if (!partial || polls.current >= 20) return;
    const t = setTimeout(() => { polls.current += 1; setSuggestTick((x) => x + 1); }, 3000);
    return () => clearTimeout(t);
  }, [partial, suggestions.data]);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const shownSuggestions = useMemo(() => (suggestions.data?.suggestions ?? []).filter((x) => !hidden.has(x.id)), [suggestions.data, hidden]);
  // The view is in the URL: every view switch is a history step (push), so Back undoes it; typing and tidying replace
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const view = readModels(params);
  const change: Change = (patch, how = 'push') => setParams((p) => patchModels(p, patch), how === 'replace' ? { replace: true, state: location.state } : undefined);
  // A deploy panel is a step in history: Back closes it, closing it goes back
  const bulkWin = useModalSteps('bulkOpened');
  // The editor is a step in history (?edit=new or ?edit=<id>): Back closes it, closing it goes back
  const editor = useUrlModal(EDIT_KEY, readEdit, withEdit);
  // The editor pre-filled from a suggestion (?edit=new&from=<id>): also a step in history
  const fromEditor = useUrlModal(FROM_KEY, readFrom, withFrom);
  const importer = useUrlModal('import', (p) => (p.get('import') === '1' ? '1' : undefined), (p, v) => { const out = new URLSearchParams(p); if (v) out.set('import', v); else out.delete('import'); return out; });
  const download = (doc: unknown, name: string) => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };
  const exportModels = async (only?: VirtualModel) => {
    try {
      const doc = await Api.exportModels(tid) as { models?: { name: string }[] };
      const day = new Date().toISOString().slice(0, 10);
      if (only) download({ ...doc, models: (doc.models ?? []).filter((x) => x.name === only.name) }, `messagevault-model-${only.name.replace(/[^\w-]+/g, '-').toLowerCase()}-${day}.json`);
      else download(doc, `messagevault-models-${day}.json`);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'fail');
    }
  };
  const afterSave = useRef<string | undefined>(undefined);
  const [removing, setRemoving] = useState<VirtualModel>();
  const list = models.data ?? [];
  useEffect(() => { if (models.data) onCount?.(models.data.length); }, [models.data, onCount]);
  const byId = useMemo(() => new Map(list.map((m) => [m.id, m])), [list]);
  // Ticked models that no longer exist are dropped once the list is known
  const selected = useMemo<ReadonlySet<string>>(() => new Set(canManage ? view.sel.filter((id) => !models.data || byId.has(id)) : []), [canManage, view.sel.join(','), models.data, byId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id: string) => change({ sel: selected.has(id) ? [...selected].filter((x) => x !== id) : [...selected, id] }, 'replace');
  const deploying = useMemo(() => (canManage && view.panel === 'bulk' && models.data ? latestItems(list, selected) : undefined), [canManage, view.panel, models.data, list, selected]);
  const editing = canManage ? editor.value : undefined;
  const fromId = canManage ? fromEditor.value : undefined;
  const fromModel = fromId ? suggestions.data?.suggestions.find((x) => x.id === fromId) : undefined;
  // A link to a suggestion that is gone (its sources changed, or it was dismissed) opens what was typed, or nothing
  const fromDraft = !!fromId && !fromModel && hasForm(draftKey(tid, suggestionDraftId(fromId)));
  useEffect(() => {
    if (fromId && suggestions.data && !fromModel && !fromDraft) {
      toast('That suggestion is no longer there; its fields may have changed', 'fail');
      setParams((p) => withFrom(p, undefined), { replace: true });
    }
  }, [fromId, suggestions.data, fromModel, fromDraft, setParams, toast]);
  const dismiss = async (sg: ModelSuggestion) => {
    setHidden((h) => new Set([...h, sg.id]));
    try {
      await Api.dismissSuggestion(tid, sg.id);
      toast(`${sg.name} dismissed`, 'ok', { label: 'Undo', onClick: () => {
        void Api.undismissSuggestion(tid, sg.id).then(() => { setHidden((h) => new Set([...h].filter((x) => x !== sg.id))); suggestions.reload(); }, (e) => toast(message(e), 'fail'));
      } });
    } catch (e) {
      setHidden((h) => new Set([...h].filter((x) => x !== sg.id)));
      toast(message(e), 'fail');
    }
  };
  const editModel = editing && editing !== NEW_MODEL ? byId.get(editing) : undefined;
  // A link to a model that is gone (or to the editor for a viewer) leaves the editor out of the URL
  useEffect(() => {
    if (editor.value && ((models.data && editor.value !== NEW_MODEL && !byId.has(editor.value)) || !canManage)) setParams((p) => withEdit(p, undefined), { replace: true });
  }, [editor.value, models.data, byId, canManage, setParams]);
  // After a save the saved model is the chosen one (the step back to the list has happened by then)
  useEffect(() => {
    if (!editor.value && afterSave.current) {
      const id = afterSave.current;
      afterSave.current = undefined;
      setParams((p) => openModel(p, id), { replace: true });
    }
  });
  // What the right side shows: the suggestion chosen, else the model chosen (one that is gone: the first)
  const suggestion = view.sug ? shownSuggestions.find((x) => x.id === view.sug) : undefined;
  const chosen = suggestion ? undefined : (view.model ? byId.get(view.model) : undefined) ?? list[0];
  // Choosing is a step in history, like choosing a class on the Data model tab
  const choose = (id: string) => { if (id !== view.model || view.sug) setParams((p) => openModel(p, id), { state: {} }); };
  const chooseSuggestion = (id: string) => { if (id !== view.sug) setParams((p) => openSuggestion(p, id), { state: {} }); };
  const switchTab = (t: ModelTab) => { if (t !== view.tab) setParams((p) => patchModels(p, { tab: t }), { state: {} }); };
  // "Open" from Settings → Data model (a toast) brings the model into view
  const revealed = useRef<string | undefined>(undefined);
  useEffect(() => {
    if ((location.state as { reveal?: boolean } | null)?.reveal && view.model && models.data && revealed.current !== location.key && byId.has(view.model)) {
      revealed.current = location.key;
      document.getElementById(`vm-${view.model}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  });
  const closeBulk = (clear: boolean) => (clear ? change({ panel: undefined, denv: undefined, sel: [] }, 'replace') : bulkWin.close((p) => patchModels(p, { panel: undefined, denv: undefined })));
  const envName = visible ? current?.name ?? 'this environment' : 'this environment';
  const showSuggested = shownSuggestions.length > 0 || partial;

  return (
    <section className={cx(embedded ? '' : 'mt-10', 'flex flex-col gap-4')} aria-labelledby="virtual-models">
      <h2 id="virtual-models" className="sr-only">Your data models</h2>
      <div className="flex items-start gap-x-6 gap-y-3 flex-wrap">
        <p className="m-0 text-small text-n8 max-w-3xl flex-1 min-w-[280px]">
          A data model of your own is made of fields of several systems that mean the same thing (a customer: CRM Name, ERP CustName, the invoice's BuyerOrganisationName). A search finds its records by any of them: the search shows them above its answer.
          You edit a draft, publish it as a version and deploy that version to an environment; each environment runs the version deployed to it.
        </p>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => importer.open('1')}>Import JSON…</Button>
            {list.length > 0 && <Button onClick={() => void exportModels()}>Export JSON</Button>}
            <Button variant="primary" onClick={() => editor.open(NEW_MODEL)}><PlusIcon size={14} />New data model</Button>
          </div>
        )}
      </div>
      {removing && (
        <Confirm question={`Delete ${removing.name}?`} action="Delete" onCancel={() => setRemoving(undefined)} onConfirm={async () => {
          try {
            await Api.deleteVirtualModel(tid, removing.id);
            toast(`${removing.name} deleted`);
            change({ sel: [...selected].filter((x) => x !== removing.id), ...(view.model === removing.id ? { model: undefined } : {}) }, 'replace');
            models.reload();
          } catch (e) {
            toast(message(e), 'fail');
          }
          setRemoving(undefined);
        }}>
          The definition and its versions go; the data it is made of stays as it is.
        </Confirm>
      )}
      {canManage && selected.size > 0 && view.panel !== 'bulk' && (
        <Surface className="px-4 py-3 flex items-center gap-3 flex-wrap mv-pop" role="group" aria-label="Selected models">
          <span className="text-small flex-1 min-w-[160px]">{selected.size} selected <span className="text-n8">· their latest versions go together</span></span>
          <Button variant="primary" onClick={() => bulkWin.open((p) => patchModels(p, { panel: 'bulk', denv: undefined }))}>Deploy selected…</Button>
          <Button variant="ghost" onClick={() => change({ sel: [] }, 'replace')}>Clear</Button>
        </Surface>
      )}
      {deploying && (deploying.items.length > 0
        ? <DeployPanel tid={tid} items={deploying.items} models={byId} skipped={deploying.skipped} picked={view.denv} onPick={(id) => change({ denv: id }, 'replace')} onClose={() => closeBulk(false)}
          onDone={() => { closeBulk(true); models.reload(); }} />
        : (
          <Confirm question="Nothing to deploy" action="Close" onCancel={() => closeBulk(false)} onConfirm={() => closeBulk(false)}>
            None of the selected models has a published version ({deploying.skipped.join(', ')}). Publish a version first.
          </Confirm>
        ))}
      {models.error && <p className="m-0 text-small text-fail">{models.error}</p>}
      {models.loading && !models.data && !models.error && <div className="p-6 grid place-items-center"><Spinner /></div>}
      {models.data && (
        <div className="grid gap-5 grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)]">
          <div className="flex flex-col gap-2 self-start min-w-0">
            <Surface className="p-1.5 max-h-[70vh] overflow-auto">
              {showSuggested && (
                <div role="group" aria-label="Suggested data models">
                  <button type="button" aria-expanded={!view.sfold} onClick={() => change({ sfold: !view.sfold })} className={cx(groupHead, 'cursor-pointer hover:text-n10')}>
                    <ChevronIcon size={12} className={cx('shrink-0 transition-transform duration-[var(--t-micro)]', view.sfold && '-rotate-90')} />
                    <span className="truncate">Suggested</span>
                    <span className="tabular-nums font-normal normal-case tracking-normal">{shownSuggestions.length}</span>
                    {partial && <span className="ml-auto inline-flex items-center gap-1.5 font-normal normal-case tracking-normal" aria-live="polite"><Spinner className="size-3" />checking</span>}
                  </button>
                  {!view.sfold && shownSuggestions.map((s) => <SuggestionItem key={s.id} s={s} chosen={suggestion?.id === s.id} onChoose={() => chooseSuggestion(s.id)} />)}
                </div>
              )}
              <div role="group" aria-label="Your data models">
                {showSuggested && (
                  <div className={cx(groupHead, 'mt-1')}>
                    <span className="truncate">Your data models</span>
                    <span className="tabular-nums font-normal normal-case tracking-normal">{list.length}</span>
                  </div>
                )}
                {list.map((m) => <ModelItem key={m.id} m={m} chosen={chosen?.id === m.id} owner={canManage} ticked={selected.has(m.id)} envs={sorted} onChoose={() => choose(m.id)} onTick={() => toggle(m.id)} />)}
                {list.length === 0 && <p className="m-0 px-3 py-2.5 text-small text-n7">None yet.</p>}
              </div>
            </Surface>
          </div>
          <div className="min-w-0">
            {suggestion && (
              <SuggestionView key={suggestion.id} s={suggestion} known={known} owner={canManage} envName={envName}
                onCreate={() => fromEditor.open(suggestion.id)} onDismiss={() => void dismiss(suggestion)} />
            )}
            {!suggestion && chosen && (
              <ModelView key={chosen.id} tid={tid} m={chosen} envs={sorted} known={known} owner={canManage} view={view} change={change} onTab={switchTab}
                onChanged={models.reload} onEdit={() => editor.open(chosen.id)} onDelete={() => setRemoving(chosen)} onExport={() => void exportModels(chosen)} />
            )}
            {!suggestion && !chosen && (
              <Surface className="px-6 py-10 flex flex-col items-center gap-3 text-center">
                <span className="size-12 rounded-full bg-n3 grid place-items-center text-n8"><SchemaIcon size={22} /></span>
                <p className="m-0 text-body font-medium">No data models of your own yet</p>
                <p className="m-0 text-small text-n8 max-w-xl">
                  {shownSuggestions.length > 0 && <><span className="text-n10 font-medium">We found {shownSuggestions.length === 1 ? '1 data model' : `${shownSuggestions.length} data models`} in your data.</span>{' '}{canManage ? 'Start from a suggestion on the left, or ' : 'They are listed on the left. '}</>}
                  {canManage
                    ? `${shownSuggestions.length > 0 ? 'start' : 'Start'} with a thing you care about, such as a customer or an order, and say which field in each system holds its name, number or date. Then one search finds it everywhere.`
                    : 'An owner of this tenant can create them.'}
                </p>
                {canManage && <Button variant="primary" onClick={() => editor.open(NEW_MODEL)}><PlusIcon size={14} />Create your first data model</Button>}
              </Surface>
            )}
          </div>
        </div>
      )}
      {editing === NEW_MODEL && fromId && (fromModel || fromDraft) && (
        <ModelEditor key={`from-${fromId}`} tid={tid} id={NEW_MODEL} draftId={suggestionDraftId(fromId)} fromSuggestion={fromId} start={fromModel ? fromSuggestion(fromModel) : emptyModel()}
          onClose={fromEditor.close} onSaved={(saved) => { afterSave.current = saved.id; models.reload(); suggestions.reload(); fromEditor.close(); }} />
      )}
      {editing === NEW_MODEL && !fromId && <ModelEditor key="new" tid={tid} id={NEW_MODEL} onClose={editor.close} onSaved={(saved) => { afterSave.current = saved.id; models.reload(); editor.close(); }} />}
      {editModel && <ModelEditor key={editModel.id} tid={tid} id={editModel.id} model={editModel} onClose={editor.close} onSaved={(saved) => { afterSave.current = saved.id; models.reload(); editor.close(); }} />}
      {importer.value && canManage && <ImportModels tid={tid} onClose={importer.close} onDone={() => models.reload()} />}
    </section>
  );
}
